import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '@/utils/api';
import { usePreferencesStore } from '@/store';
import type { UserPreferences, LLMProfileCreate } from '@/types';

const PREFERENCES_KEY = 'preferences';
const LLM_PROFILES_KEY = 'llm-profiles';

export function usePreferences() {
  const { setPreferences, setLoading } = usePreferencesStore();
  
  return useQuery({
    queryKey: [PREFERENCES_KEY],
    queryFn: async () => {
      setLoading(true);
      try {
        const data = await settingsApi.getPreferences();
        setPreferences(data);
        return data;
      } finally {
        setLoading(false);
      }
    },
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  const { setPreferences } = usePreferencesStore();
  
  return useMutation({
    mutationFn: (data: Partial<UserPreferences>) => settingsApi.updatePreferences(data),
    onSuccess: (data) => {
      setPreferences(data);
      queryClient.invalidateQueries({ queryKey: [PREFERENCES_KEY] });
    },
  });
}

export function useLLMProfiles() {
  return useQuery({
    queryKey: [LLM_PROFILES_KEY],
    queryFn: () => settingsApi.getLLMProfiles(),
  });
}

export function useCreateLLMProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LLMProfileCreate) => settingsApi.createLLMProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LLM_PROFILES_KEY] });
    },
  });
}

export function useUpdateLLMProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<LLMProfileCreate> }) =>
      settingsApi.updateLLMProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LLM_PROFILES_KEY] });
    },
  });
}

export function useDeleteLLMProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => settingsApi.deleteLLMProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LLM_PROFILES_KEY] });
    },
  });
}

const UNPROCESSED_KEY = 'unprocessed-count';

export function useUnprocessedCount() {
  return useQuery({
    queryKey: [UNPROCESSED_KEY],
    queryFn: () => settingsApi.getUnprocessedCount(),
  });
}

export function useReprocessUnprocessed() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => settingsApi.reprocessUnprocessed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [UNPROCESSED_KEY] });
    },
  });
}
